import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Store, CheckCircle, Info, AlertCircle, FileText, Shield, DollarSign, Users, BarChart3, Zap, Mail, Phone } from 'lucide-react'
import { api } from '../../../lib/api'
import toast from 'react-hot-toast'
import { useAuth } from '../../../contexts/AuthContext'

const RequestStoreOwner = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const handleSubmit = async () => {
    if (!acceptedTerms) {
      toast.error('Debes aceptar los términos y condiciones para continuar')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/request-store-owner', {})
      toast.success('Solicitud enviada exitosamente. Un administrador la revisará pronto.')
      navigate('/profile')
    } catch (error: any) {
      console.error('Error sending request:', error)
      toast.error(error.response?.data?.message || 'Error al enviar la solicitud. Intenta nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  const benefits = [
    {
      icon: Store,
      title: 'Gestiona tu Negocio',
      description: 'Administra productos, pedidos, empleados y más desde un solo lugar'
    },
    {
      icon: DollarSign,
      title: 'Aumenta tus Ventas',
      description: 'Llega a más clientes y aumenta tus ingresos con nuestra plataforma'
    },
    {
      icon: Users,
      title: 'Gestiona Empleados',
      description: 'Agrega y administra tu equipo: cocineros, despachadores y repartidores'
    },
    {
      icon: BarChart3,
      title: 'Analíticas Completas',
      description: 'Monitorea tus ventas, productos más vendidos y rendimiento del negocio'
    },
    {
      icon: Zap,
      title: 'Pedidos en Tiempo Real',
      description: 'Recibe y gestiona pedidos instantáneamente desde cualquier dispositivo'
    },
    {
      icon: Shield,
      title: 'Seguro y Confiable',
      description: 'Plataforma segura con protección de datos y transacciones'
    }
  ]

  const requirements = [
    'Ser mayor de edad',
    'Tener un negocio legalmente establecido',
    'Contar con los permisos necesarios para operar',
    'Tener una cuenta bancaria activa',
    'Comprometerse a cumplir con las políticas de la plataforma'
  ]

  const processSteps = [
    {
      step: 1,
      title: 'Envía tu Solicitud',
      description: 'Completa el formulario y envía tu solicitud para ser dueño de negocio'
    },
    {
      step: 2,
      title: 'Revisión',
      description: 'Nuestro equipo revisará tu solicitud y verificará la información'
    },
    {
      step: 3,
      title: 'Aprobación',
      description: 'Si eres aprobado, recibirás un correo con las instrucciones para continuar'
    },
    {
      step: 4,
      title: 'Configuración',
      description: 'Configura tu negocio, agrega productos y comienza a recibir pedidos'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 p-2 rounded-full text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Volver</span>
            </button>
            <h1 className="ml-4 text-xl font-bold text-gray-900">Solicitar ser Dueño de Negocio</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl sm:rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Store className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">Conviértete en Dueño de Negocio</h2>
              <p className="text-primary-100 mt-1">Únete a nuestra plataforma y haz crecer tu negocio</p>
            </div>
          </div>
          <p className="text-lg text-primary-50 max-w-2xl">
            Gestiona tu negocio de manera eficiente, aumenta tus ventas y llega a más clientes con nuestra plataforma integral.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <CheckCircle className="w-6 h-6 text-primary-600 mr-2" />
            Beneficios de ser Dueño de Negocio
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <FileText className="w-6 h-6 text-primary-600 mr-2" />
            Requisitos
          </h3>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <ul className="space-y-3">
              {requirements.map((requirement, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{requirement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Zap className="w-6 h-6 text-primary-600 mr-2" />
            Proceso de Solicitud
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative"
              >
                <div className="absolute top-4 right-4 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-sm">{step.step}</span>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2 pr-8">{step.title}</h4>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div className="mb-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Info className="w-5 h-5 text-primary-600 mr-2" />
              Tu Información
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Correo Electrónico</p>
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="text-sm font-medium text-gray-900">{user.phone || 'No proporcionado'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Terms and Submit */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-5 h-5 text-primary-600 mr-2" />
              Términos y Condiciones
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">Al enviar esta solicitud, aceptas:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Cumplir con todas las políticas y términos de servicio de la plataforma</li>
                    <li>Proporcionar información veraz y actualizada sobre tu negocio</li>
                    <li>Mantener tu cuenta y datos de seguridad actualizados</li>
                    <li>Respetar los derechos de los clientes y otros usuarios</li>
                    <li>Pagar las comisiones y tarifas establecidas por la plataforma</li>
                  </ul>
                </div>
              </div>
            </div>
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 mr-3 w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                He leído y acepto los términos y condiciones, y me comprometo a cumplir con todas las políticas de la plataforma.
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !acceptedTerms}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Enviar Solicitud
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RequestStoreOwner

